import type { PersistedClient, Persister } from '@tanstack/react-query-persist-client';
import { QueryClient } from '@tanstack/react-query';

import { currentCacheScope } from '@/shared/api/client';
import { getDb, META_KEYS, readMeta, writeMeta } from '@/shared/offline';

/**
 * Query-instellingen zijn offline-first:
 * - `networkMode: 'offlineFirst'` laat queries gewoon draaien zonder netwerk,
 *   zodat de http-client zijn Dexie-cache kan uitserveren,
 * - opnieuw proberen doet de http-client al; React Query hoeft dat niet nog eens,
 * - de cache blijft een week geldig, want een inspecteur kan dagen offline zijn.
 */
export function createQueryClient(): QueryClient {
    return new QueryClient({
        defaultOptions: {
            queries: {
                networkMode: 'offlineFirst',
                retry: false,
                staleTime: 5 * 60 * 1_000,
                gcTime: 7 * 24 * 60 * 60 * 1_000,
                refetchOnWindowFocus: false,
                refetchOnReconnect: true,
            },
            mutations: {
                networkMode: 'offlineFirst',
                retry: false,
            },
        },
    });
}

/** Wat er in de `meta`-tabel staat: de cache, met van wie en welk bedrijf hij is. */
interface StoredQueryCache {
    scope: string;
    client: PersistedClient;
}

function isStoredQueryCache(value: unknown): value is StoredQueryCache {
    return (
        typeof value === 'object' &&
        value !== null &&
        typeof (value as { scope?: unknown }).scope === 'string' &&
        typeof (value as { client?: unknown }).client === 'object' &&
        (value as { client?: unknown }).client !== null
    );
}

/**
 * Bewaart de query-cache in Dexie, zodat een koude start offline werkt - maar
 * alleen voor de persoon en het bedrijf van de sessie (`currentScope`).
 *
 * - **Bewaren** legt de scope van dit moment vast, samen met de momentopname
 *   van dit moment. Zonder sessie en tijdens het meekijken is de scope `null` en
 *   wordt er niets bewaard.
 * - **Terugzetten** gebeurt alleen als de bewaarde scope precies die van de
 *   sessie is. Een cache van iemand anders, of van een ander bedrijf, komt nooit
 *   terug in beeld.
 *
 * Er is één plek: een nieuwe scope overschrijft de vorige. Wisselen van omgeving
 * kan alleen online, dus een bewaarde cache van een vorig bedrijf heeft geen nut
 * en blijft niet liggen.
 */
export function createScopedPersister(
    currentScope: () => string | null = currentCacheScope,
): Persister {
    return {
        async persistClient(client) {
            const scope = currentScope();

            if (scope === null) {
                return;
            }

            await writeMeta(getDb(), META_KEYS.queryCache, { scope, client });
        },
        async restoreClient() {
            const scope = currentScope();
            const stored = await readMeta<unknown>(getDb(), META_KEYS.queryCache);

            if (scope === null || !isStoredQueryCache(stored) || stored.scope !== scope) {
                return undefined;
            }

            return stored.client;
        },
        async removeClient() {
            await writeMeta(getDb(), META_KEYS.queryCache, undefined);
        },
    };
}

export const dexiePersister: Persister = createScopedPersister();

/** Gooit de bewaarde query-cache weg als die niet bij `scope` hoort (`null`: altijd). */
export async function forgetPersistedQueries(scope: string | null): Promise<void> {
    const db = getDb();

    await db.transaction('rw', db.meta, async () => {
        const stored = await readMeta<unknown>(db, META_KEYS.queryCache);

        if (stored === undefined || (isStoredQueryCache(stored) && stored.scope === scope)) {
            return;
        }

        await writeMeta(db, META_KEYS.queryCache, undefined);
    });
}
